/**
 * /projects — the Projects hub. Lists the tenant's projects and links to
 * create a new one. A project is a container that groups pipelines; open one
 * to manage its pipelines.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconFolders, IconPlus } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listProjects, type ProjectOut } from "@/shared/api/projects";
import { listCardActionStyle, listCardStyle } from "@/shared/ui/listCard";
import { createdOn, creator } from "./creatorMeta";

export function ProjectsList() {
  const getToken = useAccessToken();
  const [items, setItems] = useState<ProjectOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const page = await listProjects(token);
      setItems(page.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap="md">
      {/* No heading here — the hub above owns it, and the tab says which view
          this is. */}
      <Group justify="space-between" align="center">
        <Text c="dimmed" size="sm">
          A project groups related pipelines. Open a project to create and manage its pipelines.
        </Text>
        <Button component={RouterLink} to="/projects/new" leftSection={<IconPlus size={16} />}>
          New project
        </Button>
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load projects">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No projects yet. Create your first one.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
          {items.map((p) => (
            <Card
              key={p.project_id}
              withBorder
              radius="md"
              padding="lg"
              style={{
                ...listCardStyle,
                borderTop: "3px solid var(--mantine-primary-color-filled)",
              }}
            >
              <Group gap="sm" mb="xs" wrap="nowrap">
                <ThemeIcon variant="light" size={32} radius="md">
                  <IconFolders size={18} />
                </ThemeIcon>
                <Text fw={600} truncate>
                  {p.name}
                </Text>
              </Group>
              <Text size="sm" c="dimmed" lineClamp={2}>
                {p.description ?? "No description"}
              </Text>
              <Text size="xs" c="dimmed" mt="sm" lineClamp={2}>
                Created by {creator(p)} · {createdOn(p.created_at_utc)}
              </Text>
              <Button
                mt="md"
                style={listCardActionStyle}
                variant="light"
                size="xs"
                component={RouterLink}
                to={`/projects/${p.project_id}`}
              >
                Open
              </Button>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
